import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { MonthNumber } from 'Angular/types/month-number.type';
import { DateRange } from 'Angular/features/components/date-controls/date-range/interfaces/date-range.interface';
import { IndexRange } from 'Angular/features/components/date-controls/single-date-picker/interfaces/index-range.interface';
import {
    MonthAndYear,
    SingleDatePickerItem,
} from 'Angular/features/components/date-controls/single-date-picker/interfaces/single-date-picker-item.interface';
import { SingleSelectDropdownConfig } from 'Angular/features/components/dropdowns/single-select-dropdown/interfaces/single-select-dropdown-config.interface';
import { SingleSelectDropdownItem } from 'Angular/features/components/dropdowns/single-select-dropdown/interfaces/single-select-dropdown-item.interface';

import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';

import { isValidDateForDateRange } from 'Angular/features/components/date-controls/single-date-picker/utils/single-date-picker-utils';
import {
    formatDateString,
    getDaysInMonth,
    getFirstDayOfMonth,
    isValidDate,
    LONG_MONTH_NAMES,
} from 'Angular/utils/date';
import equal from 'Angular/utils/equal/deep-equal';

/**
 * SingleDatePickerComponent
 *
 * This component allows a user to select a date within the range of years provided as input. The functionality
 * of this component is based upon the old ycSingleDatePicker component that was implemented using AngularJS.
 *
 * For selectable date ranges populate either startYear and endYear or startDate or endDate.
 *
 * Input Properties:
 *  startYear: number that is the start of the range of years for which dates should be selectable, e.g. 2010
 *  endYear: number that is the end of the range of years for which dates should be selectable, e.g. 2021
 *  startDate: Date that is the start of the range for which dates should be selectable.
 *  endYear: Date that is the end of the range for which dates should be selectable.
 *  selectedDate: Date object that is the currently selected date
 *  selectedDateRange: DateRange object that determines the date range highlighting when this component is part of
 *                     a date range component
 *  isStartOfRange: boolean which specifies the role of the date picker component as part of a date range component
 *  requireDate: if required display error message when date is not selected
 *
 * Output Properties:
 *  dateChangeHandler: EventEmitter<Date | null> that emits the new date when a date is selected, or emits null
 *                     if the datepicker is cleared out
 *
 */
@Component({
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ycn-single-date-picker',
    templateUrl: './single-date-picker.template.html',
})
export class SingleDatePickerComponent implements OnInit, AfterViewInit, OnChanges {
    readonly CURRENT_DATE: Date = new Date();

    readonly CURRENT_MONTH: MonthNumber = this.CURRENT_DATE.getMonth() as MonthNumber;

    readonly CURRENT_YEAR: number = this.CURRENT_DATE.getFullYear();

    // Generates the entries in the month dropdown of the date picker. The current month is selected initially.
    readonly MONTH_DROPDOWN_CONFIG: SingleSelectDropdownConfig = {
        title: { label: '' },
        list: LONG_MONTH_NAMES.map((month, index) => {
            return {
                label: month,
                value: index,
                selected: index === this.CURRENT_MONTH,
            };
        }),
    };

    // default startYear is 1890, same as the AngularJS component
    @Input() startYear?: number = 1890;

    // default endYear is currentYear + 5, same as the Angular JS component
    @Input() endYear?: number = this.CURRENT_YEAR + 5;

    // If endDate populated endYear and startYear should not be added
    @Input() endDate?: Date;

    // If startDate populated endYear and startYear should not be added
    @Input() startDate?: Date;

    @Input() selectedDate: Date | null = null;

    @Input() selectedDateRange: DateRange = { startDate: null, endDate: null };

    // Used to determine which part of a date range this date picker is
    @Input() isStartOfRange: boolean = true;

    @Input() inputDisabled: boolean = false;

    @Input() datePlaceholder: string = 'MM/DD/YYYY';

    @Input() blockInput: boolean = false;

    @Input() appendToBody: boolean = false;

    @Input() requireDate: boolean = false;

    @Output() dateChangeHandler = new EventEmitter<Date | null>();

    @ViewChild('singleDatePicker') el!: ElementRef;

    dateItems: SingleDatePickerItem[] = [];

    dateString: string = '';

    isOpen: boolean = false;

    // This is used in the template for generating the rows in the date picker table that are not first or last
    middleRowDateItemIndexRanges: IndexRange[] = [
        { startIndex: 7, endIndex: 14 },
        { startIndex: 14, endIndex: 21 },
        { startIndex: 21, endIndex: 28 },
    ];

    selectedDay: number | null = null;

    selectedMonth: MonthNumber = this.CURRENT_MONTH;

    selectedYear: number = this.CURRENT_YEAR;

    yearDropdownConfig: SingleSelectDropdownConfig = {
        title: { label: '' },
        list: [],
    };

    dateInvalid: boolean = false;

    constructor(private dropdownHelperService: DropdownHelperService) {}

    ngOnInit(): void {
        // Need to reassign full object to trigger SingleSelectDropdownComponent's ngOnChanges
        this.yearDropdownConfig = {
            title: { label: '' },
            list: this.generateYearDropdownItems(),
        };
        this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
    }

    ngAfterViewInit(): void {
        const autoCloseHandler = (): void => {
            if (this.isOpen) {
                this.toggleDatePicker();
            }
        };
        let dropdownElement = this.el.nativeElement;
        if (!this.appendToBody) dropdownElement = dropdownElement.parentNode;
        this.dropdownHelperService.setCloseHandler(dropdownElement, autoCloseHandler);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.startDate?.currentValue && changes.endDate?.currentValue) {
            this.yearDropdownConfig = {
                title: { label: '' },
                list: this.generateYearDropdownItems(),
            };
            // Select date limits Month and Year to avoid user searching for limits
            if (this.selectedDate === null) {
                if (this.isStartOfRange) {
                    this.monthChanged((this.startDate as Date).getMonth() as MonthNumber);
                    this.yearChanged((this.startDate as Date).getFullYear());
                } else {
                    this.monthChanged((this.endDate as Date).getMonth() as MonthNumber);
                    this.yearChanged((this.endDate as Date).getFullYear());
                }
            } else {
                // Reset selected date if invalid and set Month and Year
                const validNewDate =
                    this.selectedDate >= (this.startDate as Date) && this.selectedDate <= (this.endDate as Date);
                if (!validNewDate) {
                    this.dateSelected(null);
                    if (this.isStartOfRange) {
                        this.monthChanged((this.startDate as Date).getMonth() as MonthNumber);
                        this.yearChanged((this.startDate as Date).getFullYear());
                    } else {
                        this.monthChanged((this.endDate as Date).getMonth() as MonthNumber);
                        this.yearChanged((this.endDate as Date).getFullYear());
                    }
                }
            }
        }
        if (
            changes.selectedDate?.currentValue &&
            !equal(changes.selectedDate.currentValue, changes.selectedDate.previousValue)
        ) {
            const newDate: Date = changes.selectedDate?.currentValue;
            const validNewDate =
                this.startDate && this.endDate
                    ? newDate instanceof Date && newDate >= this.startDate && newDate <= this.endDate
                    : newDate instanceof Date &&
                      newDate.getFullYear() >= (this.startYear as number) &&
                      newDate.getFullYear() <= (this.endYear as number);
            // Only select the new date if it is within the allowable range of years, otherwise clear the datepicker
            if (validNewDate) {
                this.dateSelected(this.createDatePickerItemFromDate(newDate), false);
            } else {
                // Emit the null date so that the parent component can update the state
                this.dateSelected(null);
            }
        } else if (changes.selectedDate?.currentValue === null) {
            // When an invalid date for a parent date range component is entered, clear the selected date and date string
            this.dateSelected(null, false);
        }
    }

    toggleDatePicker(): void {
        this.isOpen = !this.isOpen;
    }

    // Defined as arrow function so this references the component inside the function
    closeDatePickerOnKeyPress = (): void => {
        this.isOpen = false;
    };

    /**
     * Given a Date object, create a SingleDatePickerItem with the same date.
     * @param date: Date to use when creating the SingleDatePickerItem
     * @returns SingleDatePickerItem with same year, month, and day as the provided date
     */
    createDatePickerItemFromDate(date: Date): SingleDatePickerItem {
        return {
            year: date.getFullYear(),
            month: date.getMonth() as MonthNumber,
            day: date.getDate(),
        } as SingleDatePickerItem;
    }

    /**
     * This function handles when a user is typing in the input box. If they clear the input box then
     * no date will be selected. If they enter a valid date in the format given by the placeholder string
     * 'MM/DD/YY' then the corresponding date picker item will be selected.
     * @param newDateString: the string entered into the input box
     */
    dateStringChanged(newDateString: string): void {
        this.isOpen = true;
        if (newDateString.trim().length === 0) {
            this.dateSelected(null);
        } else if (
            isValidDate(
                newDateString,
                this.startDate?.getFullYear() || (this.startYear as number),
                this.endDate?.getFullYear() || (this.endYear as number)
            )
        ) {
            const newDate: Date = new Date(newDateString);
            if (
                isValidDateForDateRange(
                    newDate,
                    this.selectedDateRange.startDate,
                    this.selectedDateRange.endDate,
                    this.isStartOfRange,
                    this.startDate,
                    this.endDate
                )
            ) {
                this.dateSelected(this.createDatePickerItemFromDate(newDate));
            } else {
                this.dateInvalid = true;
            }
        } else {
            this.dateInvalid = true;
        }
    }

    /**
     * Generates the entries in the year dropdown of the date picker. The current year is selected initially.
     * @returns SingleSelectDropdownItem[] that contains a dropdown item for each year in the range of
     * years between the start year and the end year that were input to the component.
     */
    generateYearDropdownItems(): SingleSelectDropdownItem[] {
        const numYears: number =
            this.startDate && this.endDate
                ? this.endDate.getFullYear() - this.startDate.getFullYear() + 1
                : (this.endYear as number) - (this.startYear as number) + 1;
        return Array(numYears)
            .fill(0) // Placeholder value to allow iteration as many times as there are years
            .map((element, index) => {
                const year = (this.startDate?.getFullYear() || (this.startYear as number)) + index;
                return {
                    label: `${year}`,
                    value: year,
                    selected: this.CURRENT_YEAR === year,
                };
            });
    }

    /**
     * Given a year and month, this function generates the date picker items for that month. Each date picker item
     * represents a day, and they are used in the template to create the rows of selectable days in the
     * date picker calendar. Days in the previous month or next month are not be selectable.
     * @param yearVal: number that is the year to generate the date picker items for
     * @param monthVal: MonthNumber (e.g. 0-11) that is the month to generate the date picker items for
     * @returns SingleDatePickerItem[] that contains an item for each day in the given month plus whatever extra
     * items are needed to fill the rows in the calendar.
     */
    generateDatePickerItems(yearVal: number, monthVal: MonthNumber): SingleDatePickerItem[] {
        const dateItems: SingleDatePickerItem[] = [];
        const numDaysInCurrentMonth: number = getDaysInMonth(yearVal, monthVal);
        // firstDayOfMonth refers to the day of the week that the first day of the month
        // occurs on. Ror example, the first day in March 2018 is a Thursday, which is represented as 4
        const firstDayOfMonth: number = getFirstDayOfMonth(yearVal, monthVal);

        // Compute the previous month and what year that previous month is a part of
        const prevMonthAndYear: MonthAndYear = this.getPrevMonthAndYear(yearVal, monthVal);
        const numDaysInPrevMonth: number = getDaysInMonth(prevMonthAndYear.year, prevMonthAndYear.month);
        // handle all the disabled days from the previous month
        for (let i = 1; i <= firstDayOfMonth; i += 1) {
            const prevDay: number = numDaysInPrevMonth - firstDayOfMonth + i;
            const prevDate: SingleDatePickerItem = {
                year: prevMonthAndYear.year,
                month: prevMonthAndYear.month,
                day: prevDay,
            };
            dateItems.push(prevDate);
        }

        // handle all the days in the current month
        for (let day = 1; day <= numDaysInCurrentMonth; day += 1) {
            const date: SingleDatePickerItem = {
                year: yearVal,
                month: monthVal,
                day,
            };
            dateItems.push(date);
        }

        // We show rows of 7 dateItems on the frontend. Usually only 5 rows are needed,
        // meaning a total of 7 * 5 = 35 dateItems. There is a possibility that long months
        // with their first day happening on a Friday or Saturday need an extra row to
        // display all the days, so 6 rows will be displayed on the frontend in that case.
        const totalDaysNeeded: number = dateItems.length > 35 ? 42 : 35;

        // Compute the next month and what year that next month is a part of
        const nextMonthAndYear: MonthAndYear = this.getNextMonthAndYear(yearVal, monthVal);
        // Handle all the disabled days for next month
        const numDisabledDays = totalDaysNeeded - dateItems.length;
        for (let nextMonthDay = 1; nextMonthDay <= numDisabledDays; nextMonthDay += 1) {
            const futureDate: SingleDatePickerItem = {
                year: nextMonthAndYear.year,
                month: nextMonthAndYear.month,
                day: nextMonthDay,
            };
            dateItems.push(futureDate);
        }
        return dateItems;
    }

    /**
     * Handles when a date is selected in the date picker.
     * @param dateItem: SingleDatePickerItem that was selected
     * @param emitDate: boolean whether or not to emit the newly selected date and trigger the dateChangeHandler.
     */
    dateSelected(dateItem: SingleDatePickerItem | null, emitDate: boolean = true): void {
        if (dateItem) {
            this.dateInvalid = false;
            // Update selected year, month, and day
            this.selectedYear = dateItem.year;
            this.selectedMonth = dateItem.month;
            this.selectedDay = dateItem.day;
            // Regenerate datepicker items based on what is selected
            this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
            // Update the selected Date object
            this.selectedDate = new Date(this.selectedYear, this.selectedMonth, this.selectedDay);
            // Populate the date string based on the selected Date object
            this.dateString = formatDateString(this.selectedDate);
            // Close the datepicker on date selection
        } else {
            // Date was invalid or cleared out
            this.selectedDate = null;
            this.dateString = '';
        }
        // Close the date picker on selection
        this.isOpen = false;
        if (emitDate) {
            this.dateChangeHandler.emit(this.selectedDate);
        }
    }

    /**
     * Handles when a month is selected in the month dropdown of the datepicker. New date items are generated
     * based on the newly selected month.
     * @param newMonth: MonthNumber that was newly selected
     */
    monthChanged(newMonth: MonthNumber): void {
        if (newMonth !== this.selectedMonth) {
            this.selectedMonth = newMonth;
            this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
        }
    }

    /**
     * Handles when a year is selected in the year dropdown of the datepicker. New date items are generated
     * based on the newly selected month.
     * @param newYear: number that was newly selected
     */
    yearChanged(newYear: number): void {
        if (newYear !== this.selectedYear) {
            this.selectedYear = newYear;
            this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
        }
    }

    /**
     * Handles when the arrow button to go to the next month is clicked. New date picker items are then
     * generated for the next month.
     */
    nextMonth(): void {
        const nextMonthAndYear: MonthAndYear = this.getNextMonthAndYear(this.selectedYear, this.selectedMonth);
        this.selectedYear = nextMonthAndYear.year;
        this.selectedMonth = nextMonthAndYear.month;
        this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
    }

    /**
     * Handles when the arrow button to go to the previous month is clicked. New date picker items are then
     * generated for the previous month.
     */
    prevMonth(): void {
        const prevMonthAndYear: MonthAndYear = this.getPrevMonthAndYear(this.selectedYear, this.selectedMonth);
        this.selectedYear = prevMonthAndYear.year;
        this.selectedMonth = prevMonthAndYear.month;
        this.dateItems = this.generateDatePickerItems(this.selectedYear, this.selectedMonth);
    }

    /**
     * Compute what month and year immediately follow a given month and year. In most cases the month
     * only needs to be incremented, but if it is the last month of the year then the next month will be 0
     * and the year is incremented.
     * @param currentYear: number that is the year of the currentMonth
     * @param currentMonth: MonthNumber to compute the next month and year for
     * @returns MonthAndYear object that contains the immediately following month and year
     */
    getNextMonthAndYear(currentYear: number, currentMonth: MonthNumber): MonthAndYear {
        if (currentMonth === 11) {
            return {
                month: 0,
                year: currentYear + 1,
            };
        }
        return {
            month: (currentMonth + 1) as MonthNumber,
            year: currentYear,
        };
    }

    /**
     * Compute what month and year immediately precede a given month and year. In most cases the month
     * only needs to be decremented, but if it is the first month of the year then the previous month will be 11
     * and the year will be decremented.
     * @param currentYear
     * @param currentMonth
     * @returns MonthAndYear object that contains the immediately preceding month and year
     */
    getPrevMonthAndYear(currentYear: number, currentMonth: MonthNumber): MonthAndYear {
        if (currentMonth === 0) {
            return {
                month: 11,
                year: currentYear - 1,
            };
        }
        return {
            month: (currentMonth - 1) as MonthNumber,
            year: currentYear,
        };
    }
}
